import type { INSTITUTIONS_REDUCER_KEY } from 'app-components/flux/institutions/institutionsTypes';
import { getAggregatorFromChannel } from 'app-components/flux/institutions/institutionsUtils';
import { createAppSelector, createWeakMapSelector } from 'app-components/utils/core/reselect';
import type RootState from 'app-components/utils/redux-store/rootState';

import { type ACCOUNTS_REDUCER_KEY, accountsSelectors } from '../accounts';
import {
  getIconForAccountType,
  type InstitutionIconInput,
  type InstitutionIconSource,
  type ResolveIconInput,
} from './institutionIconTypes';
import { getInstitutionForInstitutionLogin, getInstitutionLoginForId } from './institutionLoginsSelectors';
import type { INSTITUTION_LOGINS_REDUCER_KEY } from './institutionLoginsTypes';

type State = Pick<
  RootState,
  typeof INSTITUTION_LOGINS_REDUCER_KEY | typeof INSTITUTIONS_REDUCER_KEY | ACCOUNTS_REDUCER_KEY
>;

// static fallback
const noIcon = Object.freeze({ kind: 'fallback', logoStyle: null });
/**
 * Pure function to resolve an InstitutionIconSource from an institution or biller object.
 * Used by components that receive an institution/biller prop directly (not from the store).
 *  WeakMapSelector used to memoize responses, since responses are all dynamic objects
 */
export const resolveIconSourceFromInstitution = createWeakMapSelector(
  (input: ResolveIconInput | null | undefined) => input, // forward input
  (input: ResolveIconInput | null | undefined): InstitutionIconSource => {
    if (!input) {
      return noIcon;
    }

    // Biller with logoKey
    if ('logoKey' in input && input.logoKey) {
      return { kind: 'logoUrl', url: input.logoKey, name: input.name, logoStyle: null, isStripe: false };
    }

    const logoStyle = ('logoStyle' in input && input.logoStyle) || null;

    // Institution with logoUrl
    if ('logoUrl' in input && input.logoUrl) {
      return {
        kind: 'logoUrl',
        url: input.logoUrl,
        name: input.name,
        logoStyle: logoStyle,
        isStripe: false,
      };
    }

    // Institution with base64 logo
    if ('logo' in input && input.logo) {
      const data = input.logo.includes('base64') ? input.logo : `data:image/svg;base64,${input.logo}`;
      return {
        kind: 'base64',
        data,
        name: input.name,
        logoStyle: logoStyle,
      };
    }

    return noIcon;
  },
);

// Memoized selector for the institution-login-based resolution path
// (channel → logo → base64 → fallback).
const getIconSourceForLogin = createAppSelector(
  (state: State, id: string | undefined) => (id ? getInstitutionLoginForId(state, id) : undefined),
  getInstitutionForInstitutionLogin,
  (institutionLogin, institution): InstitutionIconSource => {
    const channel = institutionLogin?.channel;

    // Channel-based illustration overrides
    if (channel === 'ZILLOW') {
      return getIconForAccountType('REAL_ESTATE')!;
    }
    if (channel === 'KELLEY_BLUE_BOOK') {
      return getIconForAccountType('VEHICLE')!;
    }

    // Delegate to shared resolution utility for logoUrl / base64 / fallback
    const result = resolveIconSourceFromInstitution(institution);

    // Tag Stripe accounts so callers can optionally render a custom Stripe icon
    if (result.kind === 'logoUrl' && channel && getAggregatorFromChannel(channel) === 'STRIPE') {
      return { ...result, isStripe: true };
    }

    return result;
  },
);

/**
 * Unified selector for institution icon resolution.
 * Accepts `{ accountId }`, `{ institutionLoginId }`, or `{ institution }`.
 *
 * Resolution order for account-based path:
 *   1. Account type (VEHICLE → kbbVehicle, REAL_ESTATE → zillowProperty)
 *   2. Derive institutionLoginId from account → delegate to login-based path
 *
 * Resolution order for login-based path (accountId-derived or direct institutionLoginId):
 *   1. Channel overrides (ZILLOW, KELLEY_BLUE_BOOK)
 *   2. Institution logoUrl / base64 / fallback
 *
 * The institution-object path resolves directly without state access.
 */
export const getInstitutionIconSource = (state: State, input: InstitutionIconInput): InstitutionIconSource => {
  if ('accountId' in input) {
    const account = accountsSelectors.getAccountById(state, input.accountId);

    // Account type takes priority regardless of connection status
    const typeIcon = getIconForAccountType(account?.type);
    if (typeIcon) return typeIcon;

    // Derive loginId from account and delegate to memoized login-based selector
    return getIconSourceForLogin(state, account?.institutionLoginId);
  }

  if ('institutionLoginId' in input) {
    return getIconSourceForLogin(state, input.institutionLoginId);
  }

  return resolveIconSourceFromInstitution(input.institution);
};
